// src/pages/TryOn.jsx

import React, { useState, useRef, useEffect } from 'react';
import PhotoUpload from '../components/photo/PhotoUpload';
import CameraCapture from '../components/photo/CameraCapture';
import PhotoGuidelines from '../components/photo/PhotoGuidelines';
import PhotoPreview from '../components/photo/PhotoPreview';
import WorkflowIndicator from '../components/common/WorkflowIndicator';
import Analysis from '../components/analysis/Analysis';
import LoadingSpinner from '../components/common/LoadingSpinner';
// 1. New component for video upload
import VideoUpload from '../components/photo/VideoUpload';

const steps = [
  { id: 'guidelines', name: 'Guidelines' },
  { id: 'upload', name: 'Upload' },
  { id: 'preview', name: 'Preview' },
  { id: 'analysis', name: 'Analysis' },
  { id: '3d-model', name: '3D Model' },
];

const TryOn = () => {
  const [file, setFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [step, setStep] = useState('guidelines');
  const [showCamera, setShowCamera] = useState(false);
  const [fileSource, setFileSource] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [userChoices, setUserChoices] = useState(null);
  // 2. New state to track if the input is a video
  const [isInputVideo, setIsInputVideo] = useState(false);

  const currentStepIndex = steps.findIndex(s => s.id === step);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPhotoUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setPhotoUrl(null);
      };
    }
  }, [file]);

  let modelPath = '';
  if (userChoices) {
    if (userChoices.gender === 'female') {
      modelPath = '/human-templates/my-3d-model/female_model.glb';
    } else if (userChoices.gender === 'male') {
      modelPath = '/human-templates/my-3d-model/female_model.glb';
    }
  }

  const resetWorkflowState = () => {
    setFile(null);
    setPhotoUrl(null);
    setAnalysisResults(null);
    setUserChoices(null);
    setFileSource(null);
    // 3. Reset the video flag
    setIsInputVideo(false);
  };

  const handleGuidelinesConfirm = () => {
    setStep('upload');
  };

  const handleFile = (uploadedFile) => {
    setIsProcessing(true);
    resetWorkflowState();
    setTimeout(() => {
      setFile(uploadedFile);
      setStep('preview');
      setShowCamera(false);
      setFileSource('upload');
      // 4. Set the video flag based on file type
      setIsInputVideo(uploadedFile.type.startsWith('video/'));
      setIsProcessing(false);
    }, 500);
  };

  const handleCameraCapture = (capturedFile) => {
    setIsProcessing(true);
    resetWorkflowState();
    setTimeout(() => {
      setFile(capturedFile);
      setStep('preview');
      setShowCamera(false);
      setFileSource('camera');
      setIsInputVideo(false); // A camera capture is always an image
      setIsProcessing(false);
    }, 500);
  };

  const handleRetakePhoto = () => {
    resetWorkflowState();
    setShowCamera(true);
  };

  const handleReupload = () => {
    resetWorkflowState();
    fileInputRef.current.click();
  };

  const handlePreviewConfirm = () => {
    setAnalysisResults(null);
    setStep('analysis');
  };

  const handleAnalysisComplete = (poseLandmarks) => {
    setAnalysisResults(poseLandmarks);
  };

  const handleChoicesConfirm = (choices) => {
    setUserChoices(choices);
    setStep('3d-model');
  };

  const handleRedo = () => {
    resetWorkflowState();
    setStep('upload');
  };

  const handleRetry = () => {
    setAnalysisResults(null);
    setStep('analysis');
  };

  const handleStepClick = (stepId) => {
    if (!isProcessing) {
      const targetStepIndex = steps.findIndex(s => s.id === stepId);
      const currentStepIndex = steps.findIndex(s => s.id === step);

      if (targetStepIndex < currentStepIndex) {
        setAnalysisResults(null);
        setUserChoices(null);
      }
      if (stepId === 'guidelines' || stepId === 'upload') {
        resetWorkflowState();
        setStep(stepId);
      } else if (stepId === 'preview' && file) {
        setStep(stepId);
      } else if (stepId === 'analysis' && file) {
        setStep(stepId);
      } else if (stepId === '3d-model' && analysisResults && userChoices) {
        setStep(stepId);
      }
    }
  };

  const renderContent = () => {
    if (showCamera) {
      return (
        <CameraCapture
          onCapture={handleCameraCapture}
          onCancel={() => setShowCamera(false)}
        />
      );
    }
    if (isProcessing) {
      return (
        <div className="text-center text-gray-500 p-8">
          <LoadingSpinner />
          <p>Processing...</p>
        </div>
      );
    }
    switch (step) {
      case 'guidelines':
        return <PhotoGuidelines onConfirm={handleGuidelinesConfirm} />;
      case 'upload':
        // 5. Add VideoUpload as a new option
        return (
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <PhotoUpload
              onPhotoUpload={handleFile}
              onCameraCapture={() => setShowCamera(true)}
            />
            <VideoUpload onVideoUpload={handleFile} />
          </div>
        );
      case 'preview':
        // The PhotoPreview component will work for both images and videos
        if (file) {
          return (
            <PhotoPreview
              file={file}
              onConfirm={handlePreviewConfirm}
              onRetake={handleRetakePhoto}
              onReuploadClick={handleReupload}
              source={fileSource}
            />
          );
        }
        return (
          <PhotoUpload
            onPhotoUpload={handleFile}
            onCameraCapture={() => setShowCamera(true)}
          />
        );
      case 'analysis':
      case '3d-model':
        // 6. Pass the file and its type to the Analysis component
        return (
          <Analysis
            file={file}
            isInputVideo={isInputVideo}
            modelPath={modelPath}
            onAnalysisComplete={handleAnalysisComplete}
            analysisResults={analysisResults}
            onRedo={handleRedo}
            onRetry={handleRetry}
            userChoices={userChoices}
            onChoicesConfirm={handleChoicesConfirm}
          />
        );
      default:
        return null;
    }
  };

  const handleReuploadChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      handleFile(uploadedFile);
    }
    e.target.value = null;
  };

  return (
    <div className="flex flex-col items-center min-h-screen p-4 bg-gray-50">
      <main className="container p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 text-center">
          Begin Your Virtual Try-On
        </h1>
        <p className="text-lg text-gray-700 mb-10 text-center">
          Let's find your perfect fit with style and confidence.
        </p>

        <WorkflowIndicator
          steps={steps}
          currentStep={currentStepIndex}
          onStepClick={handleStepClick}
        />

        {renderContent()}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleReuploadChange}
          accept="image/*,video/*"
          className="hidden"
        />
      </main>
    </div>
  );
};

export default TryOn;